import { useEffect, useRef, useState } from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { usePrefersReducedMotion } from '../lib/hooks'

/**
 * "Chevron flow" pipeline visualization (user-selected design, option 25+).
 *
 * Five arrow-shaped chevron segments fill with an indigo→purple gradient as a
 * name-pair chip travels across them. The chevron that DECIDES the pair floods
 * green (match) or red (reject), the chip flips to a verdict pill, and a live
 * matched/rejected tally ticks up. Choreography is a tiny React state machine
 * (timeouts); all motion is CSS transitions on transform/opacity/scaleX.
 *
 * Mobile + prefers-reduced-motion render the static stage list instead.
 */

interface Stage {
  num: string
  name: string
  desc: string
  pct: string
}

const STAGES: Stage[] = [
  { num: '01', name: 'Clean up', desc: 'FC, accents, sponsor names — stripped in microseconds.', pct: '58%' },
  { num: '02', name: 'Known aliases', desc: 'PSG = Paris Saint-Germain. Curated rules answer instantly.', pct: '27%' },
  { num: '03', name: 'Similarity scan', desc: 'A neural score: are these names even close?', pct: '9%' },
  { num: '04', name: 'Lookalike trap', desc: 'Man City vs Man Utd look alike — and are kept apart.', pct: '4%' },
  { num: '05', name: 'Final verdict', desc: 'A 31-signal model makes the last call on the hard ones.', pct: '2%' },
]

interface Pair {
  a: string
  b: string
  /** Index into STAGES of the stage that decides this pair. */
  stage: number
  match: boolean
}

// Each pair exits at the stage its REAL deciding method lands on.
const PAIRS: Pair[] = [
  { a: 'Kylian Mbappé', b: 'K. Mbappe', stage: 0, match: true },
  { a: 'PSG', b: 'Paris Saint-Germain', stage: 1, match: true },
  { a: 'Lakers', b: 'Real Madrid', stage: 2, match: false },
  { a: 'Man City', b: 'Man Utd', stage: 3, match: false },
  { a: 'Spurs', b: 'Tottenham', stage: 4, match: true },
  { a: 'Bayern', b: 'Bayer 04', stage: 3, match: false },
  { a: 'Boca Jrs.', b: 'Boca Juniors', stage: 0, match: true },
  { a: 'Djokovic, N.', b: 'Novak Djokovic', stage: 1, match: true },
]

const DWELL_MS = 750 // time the chip spends over each chevron
const VERDICT_HOLD_MS = 1500 // how long the verdict state is displayed
const RESET_GAP_MS = 500 // pause between pairs

const PF_CSS = `
.pfc-seg { clip-path: polygon(0 0, calc(100% - 26px) 0, 100% 50%, calc(100% - 26px) 100%, 0 100%, 26px 50%); }
.pfc-seg-first { clip-path: polygon(0 0, calc(100% - 26px) 0, 100% 50%, calc(100% - 26px) 100%, 0 100%); }
.pfc-fill { transform-origin: left center; transform: scaleX(0); }
.pfc-fill.on { transform: scaleX(1); transition: transform .55s cubic-bezier(.3,.7,.3,1); }
.pfc-fill.instant { transform: scaleX(1); transition: none; }
@keyframes pfc-shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}
.pfc-shimmer { animation: pfc-shimmer 5.5s linear infinite; }
@keyframes pfc-pop {
  0% { transform: translate(-50%, 0) scale(.85); }
  55% { transform: translate(-50%, 0) scale(1.14); }
  100% { transform: translate(-50%, 0) scale(1); }
}
.pfc-chip-pop { animation: pfc-pop .28s cubic-bezier(.2,1.3,.4,1); }
@keyframes pfc-burst {
  0% { opacity: .9; transform: scale(.6); }
  100% { opacity: 0; transform: scale(1.6); }
}
.pfc-burst { animation: pfc-burst .6s ease-out forwards; }
`

type CSSVars = React.CSSProperties & Record<string, string | number>

function VerdictPill({ pair }: { pair: Pair }) {
  const cls = pair.match
    ? 'border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E]'
    : 'border-[#EF4444]/30 bg-[#EF4444]/10 text-[#EF4444]'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] ${cls}`}
    >
      <span className="text-dash-secondary">
        {pair.a} <span className="text-dash-muted">&harr;</span> {pair.b}
      </span>
      {pair.match ? (
        <CheckIcon className="h-3 w-3" aria-hidden="true" />
      ) : (
        <XMarkIcon className="h-3 w-3" aria-hidden="true" />
      )}
      {pair.match ? 'MATCH' : 'REJECT'}
    </span>
  )
}

/** Static list used for mobile and prefers-reduced-motion. */
function StaticView({ className = '' }: { className?: string }) {
  return (
    <ol className={`space-y-3 ${className}`}>
      {STAGES.map((stage, i) => {
        const decided = PAIRS.filter((p) => p.stage === i)
        return (
          <li key={stage.num} className="rounded-xl border border-dash-border bg-dash-surface p-5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-dash-accent/10 font-mono text-sm font-semibold text-dash-accent tabular-nums">
                {stage.num}
              </span>
              <h3 className="text-base font-semibold text-dash-text">{stage.name}</h3>
              <span className="ml-auto font-mono text-sm font-semibold text-dash-accent">
                {stage.pct}
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-dash-secondary">{stage.desc}</p>
            {decided.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {decided.map((p) => (
                  <VerdictPill key={`${p.a}-${p.b}`} pair={p} />
                ))}
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

interface FlowState {
  pairIdx: number
  /** Chevron the chip currently sits over. */
  step: number
  /** Whether the verdict is being shown at the deciding chevron. */
  verdict: boolean
}

/** Animated chevron flow for desktop. */
function ChevronFlow({ className = '' }: { className?: string }) {
  const [state, setState] = useState<FlowState>({ pairIdx: 0, step: 0, verdict: false })
  const [tally, setTally] = useState({ matched: 812_440, rejected: 391_878 })
  const timers = useRef<number[]>([])

  useEffect(() => {
    const schedule = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms))
    }

    const runPair = (pairIdx: number) => {
      const pair = PAIRS[pairIdx % PAIRS.length]
      // step through chevrons 0..pair.stage
      for (let k = 0; k <= pair.stage; k++) {
        schedule(() => setState({ pairIdx, step: k, verdict: false }), k * DWELL_MS)
      }
      const verdictAt = pair.stage * DWELL_MS + DWELL_MS
      schedule(() => {
        setState({ pairIdx, step: pair.stage, verdict: true })
        setTally((t) =>
          pair.match
            ? { ...t, matched: t.matched + 1 }
            : { ...t, rejected: t.rejected + 1 },
        )
      }, verdictAt)
      schedule(() => runPair(pairIdx + 1), verdictAt + VERDICT_HOLD_MS + RESET_GAP_MS)
    }

    runPair(0)
    const saved = timers.current
    return () => saved.forEach(clearTimeout)
  }, [])

  const pair = PAIRS[state.pairIdx % PAIRS.length]
  const chipLeft = `${((state.step + 0.5) / STAGES.length) * 100}%`
  const verdictColor = pair.match ? '#22C55E' : '#EF4444'

  return (
    <div className={className} aria-hidden="true">
      {/* Live tally */}
      <div className="mb-4 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-dash-muted">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22C55E]" />
          </span>
          Live decision stream
        </span>
        <div className="flex gap-5 font-mono text-xs tabular-nums">
          <span className="text-[#22C55E]">✓ {tally.matched.toLocaleString()} matched</span>
          <span className="text-[#EF4444]">✕ {tally.rejected.toLocaleString()} rejected</span>
        </div>
      </div>

      {/* Chip lane */}
      <div className="relative h-12">
        <div
          key={`${state.pairIdx}-${state.verdict ? 'v' : 'n'}`}
          className={`absolute top-1/2 z-10 -translate-y-1/2 whitespace-nowrap rounded-full border px-3.5 py-1.5 font-mono text-xs shadow-lg shadow-black/50 ${
            state.verdict ? 'pfc-chip-pop font-semibold' : 'border-dash-border bg-dash-surface text-dash-secondary'
          }`}
          style={{
            left: chipLeft,
            transform: 'translate(-50%, -50%)',
            transition: 'left .6s cubic-bezier(.45,.05,.35,1)',
            ...(state.verdict
              ? {
                  backgroundColor: `${verdictColor}1f`,
                  borderColor: `${verdictColor}80`,
                  color: verdictColor,
                }
              : {}),
          }}
        >
          {state.verdict ? (
            <span className="inline-flex items-center gap-1.5">
              {pair.match ? (
                <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {pair.match ? 'MATCH' : 'NO MATCH'}
            </span>
          ) : (
            <>
              {pair.a} <span className="text-dash-muted">&harr;</span> {pair.b}
            </>
          )}
        </div>
      </div>

      {/* Chevron row */}
      <div className="mt-1 grid grid-cols-5 gap-1.5">
        {STAGES.map((stage, i) => {
          const isDecider = state.verdict && i === pair.stage
          const passed = i < state.step || (i === state.step && !state.verdict) || isDecider
          const fillColor = isDecider
            ? pair.match
              ? 'linear-gradient(90deg, rgba(34,197,94,.42), rgba(34,197,94,.18))'
              : 'linear-gradient(90deg, rgba(239,68,68,.42), rgba(239,68,68,.18))'
            : 'linear-gradient(90deg, rgba(99,102,241,.38), rgba(168,85,247,.26))'
          return (
            <div
              key={stage.num}
              className={`${i === 0 ? 'pfc-seg-first' : 'pfc-seg'} relative overflow-hidden bg-[#141417] px-7 pb-5 pt-4 text-center`}
              style={{ minHeight: 148 } as CSSVars}
            >
              {/* gradient fill sweeping in */}
              <div
                className={`pfc-fill absolute inset-0 ${passed ? 'on' : ''}`}
                style={{ background: fillColor }}
              />
              {/* ambient shimmer */}
              <div className="pointer-events-none absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-white/[.04] to-transparent pfc-shimmer" />
              {/* verdict burst */}
              {isDecider && (
                <div
                  className="pfc-burst pointer-events-none absolute inset-0"
                  style={{
                    background: `radial-gradient(circle at 50% 50%, ${verdictColor}33, transparent 65%)`,
                  }}
                />
              )}
              <div className="relative">
                <div className="font-mono text-[10px] tracking-widest text-dash-accent">{stage.num}</div>
                <h3 className="mt-1 text-sm font-semibold text-dash-text">{stage.name}</h3>
                <p className="mt-1 text-[11px] leading-snug text-dash-secondary">{stage.desc}</p>
                <div className="mt-2 font-mono text-lg font-semibold tabular-nums bg-gradient-to-r from-dash-accent to-dash-accent-2 bg-clip-text text-transparent">
                  {stage.pct}
                </div>
                <div className="text-[10px] text-dash-muted">decided here</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function PipelineFlow() {
  const reduced = usePrefersReducedMotion()

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
      <style>{PF_CSS}</style>

      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight text-dash-text sm:text-4xl">
          Watch a decision happen
        </h2>
        <p className="mt-4 text-lg text-dash-secondary">
          Every pair races through five checkpoints. Most are decided instantly &mdash; the AI only
          wakes up for the hard ones.
        </p>
      </div>

      <div className="mt-14">
        {reduced ? (
          <StaticView />
        ) : (
          <>
            <StaticView className="lg:hidden" />
            <ChevronFlow className="hidden lg:block" />
          </>
        )}
      </div>

      <p className="mx-auto mt-8 max-w-xl text-center text-sm text-dash-muted">
        85% of pairs stop at the first two checkpoints &mdash; the ML stages only run when the fast
        paths are unsure.
      </p>
    </section>
  )
}
